<script setup lang="ts">
import { computed } from 'vue';

import { AppArrowScroller, AppButton } from '@libs/ui';

import type {
  AccommodationUpsellCardData,
  ItineraryAccommodationUpsellLoaderSegment,
  ItineraryAccommodationUpsellSegment,
} from '../../../../models';
import { ItinerarySegmentType } from '../../../../models';

interface AccommodationUpsellSegmentLayoutProps {
  segment:
    | ItineraryAccommodationUpsellSegment
    | ItineraryAccommodationUpsellLoaderSegment;
}

const props = defineProps<AccommodationUpsellSegmentLayoutProps>();

interface AccommodationUpsellSegmentLayoutEmits {
  (eventName: 'navigate-to-hotel-selection'): void;
}

const emit = defineEmits<AccommodationUpsellSegmentLayoutEmits>();

const fakeProductCards = Array.from({ length: 10 }, () => undefined);

const items = computed<AccommodationUpsellCardData[] | undefined[]>(() => {
  if (props.segment.segmentType === ItinerarySegmentType.AccommodationUpsell) {
    return props.segment.productCardData;
  }
  return fakeProductCards;
});
</script>

<template>
  <div class="accommodation-upsell-segment-layout">
    <div class="segment-header">
      <span class="primary">{{ $t('planner.book_flight_plus_hotel') }}</span>
      <span class="secondary">
        {{ $t('planner.combine_flight_plus_hotel') }}
      </span>
    </div>

    <AppArrowScroller
      class="carousel"
      :items="items"
      :item-key-selector="(_, index) => index"
      :arrow-vertical-position="194"
      :display-dashed-border="false"
      :print-mode="false"
      :left-arrow-aria-label="$t('a11y.left_arrow_previous_suggestions')"
      :right-arrow-aria-label="$t('a11y.right_arrow_next_suggestions')"
      scrollbar-theme="scrollbar-violet"
    >
      <template #item="{ item, index }">
        <slot name="upsell-card" :item="item" :index="index" />
      </template>

      <template #footer>
        <div class="footer-button">
          <AppButton
            type="jungle"
            size="medium"
            state="enabled"
            @click="emit('navigate-to-hotel-selection')"
          >
            {{ $t('planner.view_all_accommodations') }}
          </AppButton>
        </div>
      </template>
    </AppArrowScroller>
  </div>
</template>

<style scoped lang="scss">
.accommodation-upsell-segment-layout {
  padding: var(--spacing-24);
  margin: 0 var(--spacing-24) var(--spacing-24);
  background-color: var(--color-brand-violet);

  @include for-less-or-equal-than-899-px {
    padding: var(--spacing-16);
    margin: 0 0 var(--spacing-16) 0;
  }

  .segment-header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-8);
    margin-bottom: var(--spacing-16);

    text-align: center;
    color: var(--color-white);

    .primary {
      @include font-heading-4;

      @include for-less-or-equal-than-899-px {
        @include font-heading-5;
      }
    }

    .secondary {
      @include font-label-l-light;

      @include for-less-or-equal-than-899-px {
        @include font-label-s-light;
      }
    }
  }

  .carousel {
    .card-spacing {
      margin-left: var(--spacing-12);
    }

    .footer-button {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: var(--spacing-16);

      @include for-less-or-equal-than-899-px {
        margin-top: var(--spacing-12);
      }
    }
  }
}
</style>
